import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Draws a Sprite2D: a CanvasItem2D whose body is a quad sized to the texture's
 * pixels (1 px = 1 world unit). The region and frame math is shared with Sprite3D
 * in `r3f/spriteFrame`. Flipping stays here: 2D mirrors by mesh scale, which
 * flips the texture too, as in Godot, where 3D negates the UV.
 */
import { useMemo, useState } from 'react';
import { CanvasItem2D } from '../../../r3f/components/CanvasItem2D';
import { frameSizePx, frameUvWindow } from '../../../r3f/spriteFrame';
import { useCanvasTextureSampler, useNode2DTextureSampler } from '../../../r3f/canvasItemTextureSampler';
import { CanvasItemSurfaceSource } from '../../../r3f/canvasItemSurface';
import { canvasMapProps, useWindowedCanvas2DTexture } from '../../../r3f/canvas2DTextureDecode';
import { materialProgramInputs } from '../../../r3f/materialProgramInputs';
import { useSceneResources } from '../../../r3f/SceneResourcesContext';
import { useAnimatedValue } from '../../../r3f/contexts/AnimatedValueContext';
import { useTexture2D } from '../../../resources/useTexture2D';
import { MissingResourcePlaceholder } from '../../../r3f/components/MissingResourcePlaceholder';
export function Sprite2D({ node, children }) {
    const props = node.properties;
    const { externalResources, internalResources } = useSceneResources();
    // An active AnimationPlayer can drive the sheet `frame` (ADR-0016), and `null`
    // means none does. The registry carries a numeric tuple (ADR-0017), here a 1-tuple.
    const animatedFrame = useAnimatedValue('frame', (v) => v[0] ?? null);
    const { texture: sourceTexture, size: reportedSize, missing: textureMissing, canvasTexture, } = useTexture2D(props.texture, externalResources, internalResources);
    // With `texture_repeat` Disabled, an overrunning `region_rect` stretches its edge texels,
    // where Sprite3D tiles. A CanvasTexture's own filter and repeat win over the item's.
    const sampler = useCanvasTextureSampler(useNode2DTextureSampler(props), canvasTexture?.texture);
    const [surface, setSurface] = useState(null);
    // A driven `frame` overrides the authored `frame` and any authored
    // `frame_coords` (in Godot the two are the same value), so the animation wins.
    const frameWindow = useMemo(() => {
        const frameProps = animatedFrame !== null ? { ...props, frame: animatedFrame, frame_coords: undefined } : props;
        return frameUvWindow(sourceTexture ?? undefined, frameProps);
    }, [sourceTexture, props, animatedFrame]);
    const drawnTexture = useWindowedCanvas2DTexture(sourceTexture, sampler, frameWindow);
    // Quad size in pixels (1 px = 1 world unit in the 2D canvas).
    const { width, height } = useMemo(() => frameSizePx(reportedSize, props), [reportedSize, props]);
    // A fragment either way, so the item keeps its place when the source mounts.
    return (_jsxs(_Fragment, { children: [_jsx(CanvasItem2D, { node: node, props: props, surface: surface, body: ({ color, opacity }, _material, pixelMaterial) => 
                // No texture draws nothing, as Godot's draw returns early (`sprite_2d.cpp:159`). The
                // placeholder is for a texture that is set but never draws.
                textureMissing ? (_jsx(MissingResourcePlaceholder, { shape: "plane", name: node.name })) : drawnTexture ? (_jsx(QuadMesh, { texture: drawnTexture, color: color, opacity: opacity, width: width, height: height, props: props, pixelMaterial: pixelMaterial })) : null, children: children }), canvasTexture?.texture.normalTexture && (_jsx(CanvasItemSurfaceSource, { slot: canvasTexture, sampler: sampler, onSurface: setSurface }))] }));
}
function QuadMesh({ texture, color, opacity, width, height, props, pixelMaterial, }) {
    // Quad centre in Godot 2D space (+Y down), then Y-negated. Centred, it sits at
    // `offset`. Otherwise the top-left does, so the centre is offset + (w/2, h/2).
    const cgx = props.offset.x + (props.centered ? 0 : width / 2);
    const cgy = props.offset.y + (props.centered ? 0 : height / 2);
    const meshScale = [props.flip_h ? -1 : 1, props.flip_v ? -1 : 1, 1];
    // The map is here at the first compile, but the define can change: a 3D-content
    // ViewportTexture multiplies linearly where a canvas-content one and a `res://` file
    // multiply in sRGB, and a swap changes the program (`materialProgramInputs.ts`).
    const program = materialProgramInputs({
        props: {
            ...canvasMapProps(texture),
            color,
            opacity,
            transparent: true,
            depthWrite: false,
        },
        merge: [pixelMaterial],
    });
    return (_jsxs("mesh", { position: [cgx, -cgy, 0], scale: meshScale, children: [_jsx("planeGeometry", { args: [width, height] }), _jsx("meshBasicMaterial", { ...program.props }, program.key)] }));
}
//# sourceMappingURL=Component.js.map